import React from 'react';
import { compose } from 'redux';
import classNames from 'classnames';
import algoliaInsights from 'search-insights';
import { string, func, bool } from 'prop-types';
import { ensureListing } from '../../util/data';
import { formatMoney } from '../../util/currency';
import { createSlug } from '../../util/urlHelpers';
import { displayPrice } from '../../util/configHelpers';
import { types as sdkTypes } from "../../util/sdkLoader";
import { PROGRAMMING_LANUGAGES } from '../../util/constants';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { TUTOR, TUTOR_MORE_THAN_TEN_YEARS } from '../../util/types';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { AspectRatioWrapper, AvatarLarge, Button, InlineTextButton, ResponsiveImage } from '../../components';

import css from './AlgoliaListingCard.module.css';
import { LESS_THAN_A_YEAR } from '../../util/constant';

const MAX_LENGTH_FOR_WORDS_IN_TITLE = 25;

// Initialize Algolia Insights.
algoliaInsights('init', {
    appId: process.env.REACT_APP_APPLICATION_ID,
    apiKey: process.env.REACT_APP_ALGOLIA_ADMIN_API_KEY,
});

const { Money } = sdkTypes;

const formatTitle = (title, maxLength) => {
    const trimedTitle = title.trim();
    return trimedTitle.length > maxLength ? title.slice(0, maxLength).concat("...") : title;
};

const createListingURL = (routes, listing, objectID) => {
    const id = listing.id;
    const slug = createSlug(listing.title);

    const linkProps = {
        name: 'ListingPage',
        params: { id, slug },
    };

    return createResourceLocatorString(linkProps.name, routes, linkProps.params, {algoliaObjectId: (objectID)});
};

const priceData = (price, currency, intl) => {
    if (price && price.currency === currency) {
        const formattedPrice = formatMoney(intl, price);
        return { formattedPrice, priceTitle: formattedPrice };
    } else if (price) {
        return {
            formattedPrice: intl.formatMessage(
                { id: 'ListingCard.unsupportedPrice' },
                { currency: price.currency }
            ),
            priceTitle: intl.formatMessage(
                { id: 'ListingCard.unsupportedPriceTitle' },
                { currency: price.currency }
            ),
        };
    }
    return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
    const { price, publicData, config, intl } = props;
    const { listingType } = publicData || {};
    const validListingTypes = config.listing.listingTypes;
    const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
    const showPrice = displayPrice(foundListingTypeConfig);
    if (!showPrice && price) {
        return null;
    }

    const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
    const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

    return (
        <div className={css.price}>
            <div className={css.priceValue} title={priceTitle}>
                {formattedPrice}
            </div>
            {isBookable ? (
                <div className={css.perUnit}>
                    <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
                </div>
            ) : null}
        </div>
    );
};

const PriceRangeMaybe = props => {
    const { price, publicData, config, intl } = props;
    const { listingType } = publicData || {};
    const validListingTypes = config.listing.listingTypes;
    const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
    const showPrice = displayPrice(foundListingTypeConfig);
    if (!showPrice && price) {
        return null;
    }

    const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

    return (
        <div className={css.price}>
            <div className={css.priceValue} title={priceTitle}>
                {formattedPrice}
            </div>
        </div>
    );
};

export const AlgoliaListingCardComponent = props => {
    const {
        className,
        rootClassName,
        intl,
        hit,
        config,
        currentUser,
        userType,
        history,
        isSearchQuery,
        indexName,
        searchQuery
    } = props;

    const routeConfiguration = useRouteConfiguration();
    const isTutorMaybe = userType == TUTOR;
    const classes = classNames(rootClassName || css.root, className, !isTutorMaybe && css.studentCardWrapper);
    const { author = {}, createdAt, metaData, title, description, price, images, publicData, objectID, __position, __queryID } = hit || {};
    const { displayName } = (author && author.id && author?.attributes?.profile) || {};
    const listingId = objectID;
    const listing = {
        id: listingId,
        title,
        description,
        metaData,
        createdAt,
        publicData,
        price
    };
    const marketplaceCurrency = config?.currency;
    const firstImage = images.url;
    const variants = firstImage ? [images.variant] : [];

    const ensuredListing = ensureListing(listing);
    const { publicData: listingPublicData } = ensuredListing || {};
    const { languageToTeach = [], tutorExperience, languageToLearn = [], timezone = null, urgencyLevel = {}, aboutUs = "", studentBudgetRange = [] } = listingPublicData || {};
    const yearLess = intl.formatMessage({ id: "SingleSelect.lessThanYearText" });
    const releventExperience = tutorExperience == TUTOR_MORE_THAN_TEN_YEARS ? intl.formatMessage({ id: "AlgoliaListingCard.yeardOfExperience" }, { experience: '10+' }) : tutorExperience == LESS_THAN_A_YEAR ? yearLess : intl.formatMessage({ id: "AlgoliaListingCard.yeardOfExperience" }, { experience: tutorExperience });
    const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
    } = config.layout.listingImage;

    const onOverListingLink = () => {
        // Enforce preloading of ListingPage (loadable component)
        const { component: Page } = findRouteByRouteName('ListingPage', routeConfiguration);
        // Loadable Component has a "preload" function.
        if (Page.preload) {
            Page.preload();
        }
    };

    const languageData = PROGRAMMING_LANUGAGES.find(item => item.label == languageToLearn[0]);

    return (
        <div className={classes}>
            {isTutorMaybe ?
                <div>
                    <div
                        className={css.clickWrapper}
                        tabIndex={0}
                        onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();

                            if (isSearchQuery) {
                                algoliaInsights('clickedObjectIDsAfterSearch', {
                                    userToken: (currentUser && currentUser.id) ? currentUser.id.uuid : 'anonymous-user',
                                    eventName: 'Product Clicked',
                                    index: indexName,
                                    objectIDs: [objectID],
                                    queryID: __queryID,
                                    positions: [__position],
                                    addtionalData: {
                                        UserID: (currentUser && currentUser.id) ? currentUser.id.uuid : '',
                                        Timestamp: Date.now(),
                                        ListingID: (listing && listing.id && listing?.id?.uuid),
                                        searchQuery: searchQuery
                                    }
                                });
                            } else {
                                algoliaInsights('clickedObjectIDs', {
                                    userToken: (currentUser && currentUser.id) ? currentUser.id.uuid : 'anonymous-user',
                                    eventName: 'Product Clicked',
                                    index: indexName,
                                    objectIDs: [objectID],
                                    queryID: __queryID,
                                    positions: [__position],
                                    addtionalData: {
                                        UserID: (currentUser && currentUser.id) ? currentUser.id.uuid : '',
                                        Timestamp: Date.now(),
                                        ListingID: (listing && listing.id && listing?.id?.uuid),
                                    }
                                });
                            }

                            // ManageListingCard contains links, buttons and elements that are working with routing.
                            // This card doesn't work if <a> or <button> is used to wrap events that are card 'clicks'.
                            //
                            // NOTE: It might be better to absolute-position those buttons over a card-links.
                            // (So, that they have no parent-child relationship - like '<a>bla<a>blaa</a></a>')
                            history.push(createListingURL(routeConfiguration, listing, objectID));
                        }}
                        onMouseOver={onOverListingLink}
                        onTouchStart={onOverListingLink}
                    >
                        <AspectRatioWrapper className={classNames(userType == TUTOR && css.tutorCard, css.imageAspectRatio)} width={aspectWidth} height={aspectHeight}>
                            <ResponsiveImage
                                rootClassName={css.rootForImage}
                                alt={title}
                                imagewithURL={firstImage}
                                variants={variants}
                                sizes={""}
                            />
                        </AspectRatioWrapper>

                        <div className={css.info}>
                            <div className={css.expectImgWrapper}>
                                {Array.isArray(languageToTeach) && languageToTeach.map(lang => {
                                    const languageData = PROGRAMMING_LANUGAGES.find(item => item.label == lang);
                                    return (
                                        <ResponsiveImage
                                            alt={languageData?.value}
                                            imagewithURL={languageData?.image}
                                            variants={[]}
                                            key={languageData?.value}
                                        />
                                    )
                                })}
                            </div>

                            <PriceMaybe price={new Money(price.amount * 100, price.currency)} publicData={publicData} config={config} intl={intl} />

                            <div className={css.mainInfo}>
                                <div className={css.titleWrapper}>
                                    <InlineTextButton
                                        onClick={event => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            history.push(createListingURL(routeConfiguration, listing));
                                        }}
                                    >
                                        {formatTitle(title, MAX_LENGTH_FOR_WORDS_IN_TITLE)}
                                    </InlineTextButton>
                                </div>
                            </div>

                            <p className={css.tutorName}>
                                <span>{displayName}</span> | <span className={css.experience}>{releventExperience}</span>
                            </p>
                        </div>
                    </div>
                </div>
                :
                <div className={css.studentCard}>
                    <div className={css.profileHeader}>
                        <div className={css.profileImage}>
                            <AvatarLarge className={css.mainProfile} user={author} />
                            <div className={css.languageImage}>
                                <ResponsiveImage
                                    alt={"Language Image"}
                                    imagewithURL={languageData?.image}
                                    variants={[]}
                                />
                            </div>
                        </div>

                        <div className={css.profileName}>{displayName}.</div>
                    </div>

                    <div className={css.studentCardContent}>
                        <div className={css.budget}>
                            <FormattedMessage id={"ManageListingPage.StudentBudgetRange"} values={{
                                // minPrice: <PriceRangeMaybe price={new Money(studentBudgetRange[0] * 100, marketplaceCurrency)} publicData={publicData} config={config} intl={intl} />,
                                // maxPrice: <PriceRangeMaybe price={new Money(studentBudgetRange[1] * 100, marketplaceCurrency)} publicData={publicData} config={config} intl={intl} />,
                                minPrice: studentBudgetRange[0],
                                maxPrice: studentBudgetRange[1],
                            }}
                            />
                        </div>
                        <div className={css.details}>
                            {languageToLearn[0]} {Object.keys(urgencyLevel).length ? "|" : null} {urgencyLevel.label}
                        </div>
                        <div className={css.description}>
                            <FormattedMessage id={'ManageListingPage.aboutUsLabel'} values={{ aboutus: aboutUs }} />
                        </div>
                        <div className={css.viewRequestButtonWrapper}>
                            <Button
                                onClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();

                                    if (isSearchQuery) {
                                        algoliaInsights('clickedObjectIDsAfterSearch', {
                                            userToken: (currentUser && currentUser.id) ? currentUser.id.uuid : 'anonymous-user',
                                            eventName: 'Product Clicked',
                                            index: indexName,
                                            objectIDs: [objectID],
                                            queryID: __queryID,
                                            positions: [__position],
                                            addtionalData: {
                                                UserID: (currentUser && currentUser.id) ? currentUser.id.uuid : '',
                                                Timestamp: Date.now(),
                                                ListingID: (listing && listing.id && listing?.id?.uuid),
                                                searchQuery: searchQuery
                                            }
                                        });
                                    } else {
                                        algoliaInsights('clickedObjectIDs', {
                                            userToken: (currentUser && currentUser.id) ? currentUser.id.uuid : 'anonymous-user',
                                            eventName: 'Product Clicked',
                                            index: indexName,
                                            objectIDs: [objectID],
                                            queryID: __queryID,
                                            positions: [__position],
                                            addtionalData: {
                                                UserID: (currentUser && currentUser.id) ? currentUser.id.uuid : '',
                                                Timestamp: Date.now(),
                                                ListingID: (listing && listing.id && listing?.id?.uuid),
                                            }
                                        });
                                    }

                                    // ManageListingCard contains links, buttons and elements that are working with routing.
                                    // This card doesn't work if <a> or <button> is used to wrap events that are card 'clicks'.
                                    //
                                    // NOTE: It might be better to absolute-position those buttons over a card-links.
                                    // (So, that they have no parent-child relationship - like '<a>bla<a>blaa</a></a>')
                                    history.push(createListingURL(routeConfiguration, listing));
                                }}
                                className={css.viewRequestButton}
                            >
                                <FormattedMessage id='AlgoliaListingCard.StudentViewRequestTitle' />
                            </Button>
                        </div>
                    </div>
                </div>
            }
        </div>
    );
};

AlgoliaListingCardComponent.defaultProps = {
    className: null,
    rootClassName: null,
    renderSizes: null,
    setActiveListing: null,
    showAuthorInfo: true,
};

AlgoliaListingCardComponent.propTypes = {
    className: string,
    rootClassName: string,
    intl: intlShape.isRequired,
    // hit: propTypes.listing.isRequired,
    showAuthorInfo: bool,

    // Responsive image sizes hint
    renderSizes: string,

    setActiveListing: func,
};

export default compose(injectIntl)(AlgoliaListingCardComponent);
